/**
 * HTTP client for the Luminis Cloudflare Worker.
 *
 * Chat uses the protocol-framed SSE proxy (`?format=protocol`), so reasoning and
 * content deltas have the same shape as the mock daemon.
 */
import type {
  ChatDelta,
  ChatDone,
  ChatError,
  DeviceInfo,
  ErrorCode,
  EventPayload,
  ModelSelection,
  ModelsResponse,
  PairBeginResponse,
  PairCompleteResponse,
  PushSubscribePayload,
  ThinkingEffort,
  ThinkingMode,
} from '@luminis/protocol';
import { randomId } from '../id';
import { ChatSSEParser, type ChatStreamEvent } from '../sse';

export class WorkerApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly retryable: boolean;

  constructor(
    code: ErrorCode,
    message: string,
    options: { status?: number; retryable?: boolean } = {},
  ) {
    super(message);
    this.name = 'WorkerApiError';
    this.code = code;
    this.status = options.status ?? 500;
    this.retryable = options.retryable ?? false;
  }
}

export interface WorkerHealth {
  ok: boolean;
  service: string;
  version: string;
  time: number;
  details?: Record<string, unknown>;
}

export interface WorkerHealthDetails {
  protocol_version?: number;
  worker_version?: string;
  environment?: string;
  features?: Record<string, boolean>;
  llm?: { configured?: boolean; default_model?: string; allowed_models?: string[] };
  web_push?: { configured?: boolean; send_enabled?: boolean; vapid_public_key?: string };
}

export interface LlmChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  reasoning_content?: string;
}

export interface LlmChatRequest {
  messages: LlmChatMessage[];
  model?: ModelSelection;
  thinking?: ThinkingMode;
  effort?: ThinkingEffort;
  conversation_id?: string;
}

export interface ChatStreamHandlers {
  onDelta(delta: ChatDelta): void;
  onDone(done: ChatDone): void;
  onError(error: ChatError): void;
  onAbort?(): void;
}

export interface ChatStreamHandle {
  abort(): void;
}

export interface WorkerClientOptions {
  baseUrl: string;
  token?: string;
  fetchImpl?: typeof fetch;
}

interface ErrorEnvelope {
  ok?: boolean;
  error?: { code?: string; message?: string; retryable?: boolean };
}

const KNOWN_ERROR_CODES = new Set<ErrorCode>([
  'AUTH_REQUIRED',
  'AUTH_INVALID',
  'APPROVAL_REQUIRED',
  'APPROVAL_EXPIRED',
  'APPROVAL_DENIED',
  'RISK_BLOCKED',
  'TOOL_NOT_FOUND',
  'EXEC_TIMEOUT',
  'PLUGIN_INVALID',
  'RATE_LIMIT',
  'INVALID_REQUEST',
  'NOT_FOUND',
  'CONFLICT',
  'INTERNAL',
]);

function errorFromResponse(status: number, body: ErrorEnvelope | null): WorkerApiError {
  const rawCode = body?.error?.code;
  const code: ErrorCode =
    rawCode && KNOWN_ERROR_CODES.has(rawCode as ErrorCode) ? (rawCode as ErrorCode) : 'INTERNAL';
  const message = body?.error?.message ?? `请求失败（HTTP ${status}）`;
  return new WorkerApiError(code, message, {
    status,
    retryable: body?.error?.retryable ?? (status >= 500 || status === 429),
  });
}

export class WorkerClient {
  readonly baseUrl: string;
  private readonly token: string | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: WorkerClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/u, '');
    this.token = options.token;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  withToken(token: string): WorkerClient {
    return new WorkerClient({ baseUrl: this.baseUrl, token, fetchImpl: this.fetchImpl });
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return {
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      ...extra,
    };
  }

  private async requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        headers: this.headers({
          'Content-Type': 'application/json',
          ...(init.headers as Record<string, string> | undefined),
        }),
      });
    } catch (error) {
      throw new WorkerApiError('INTERNAL', '无法连接 Worker（网络错误）', {
        retryable: true,
        status: 0,
      });
    }
    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!response.ok) throw errorFromResponse(response.status, parsed as ErrorEnvelope | null);
    return parsed as T;
  }

  async health(): Promise<WorkerHealth> {
    return this.requestJson<WorkerHealth>('/v1/health', { method: 'GET' });
  }

  async pairBegin(code: string, device: DeviceInfo): Promise<PairBeginResponse> {
    return this.requestJson<PairBeginResponse>('/v1/pair/begin', {
      method: 'POST',
      body: JSON.stringify({ code, device }),
    });
  }

  async pairComplete(
    code: string,
    device: DeviceInfo,
    signature?: string,
  ): Promise<PairCompleteResponse> {
    return this.requestJson<PairCompleteResponse>('/v1/pair/complete', {
      method: 'POST',
      body: JSON.stringify({ code, device, ...(signature ? { signature } : {}) }),
    });
  }

  async models(): Promise<ModelsResponse> {
    return this.requestJson<ModelsResponse>('/v1/models', { method: 'GET' });
  }

  async subscribePush(subscription: PushSubscribePayload): Promise<{ subscription_count: number }> {
    return this.requestJson<{ subscription_count: number }>('/v1/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  /** Stream a chat completion; protocol frames are parsed into typed callbacks. */
  streamChat(
    request: LlmChatRequest,
    handlers: ChatStreamHandlers,
    options: { messageId?: string; conversationId?: string } = {},
  ): ChatStreamHandle {
    const controller = new AbortController();
    const messageId = options.messageId ?? randomId('msg_');
    const selection: ModelSelection = request.model ?? {
      provider: 'deepseek',
      model: 'deepseek-flash',
    };
    const parser = new ChatSSEParser({ mode: 'protocol', messageId, model: selection });

    const dispatch = (events: ChatStreamEvent[]): void => {
      for (const event of events) {
        if (event.type === 'delta') handlers.onDelta(event.delta);
        else if (event.type === 'done') handlers.onDone(event.done);
        else handlers.onError(event.error);
      }
    };

    const run = async (): Promise<void> => {
      let response: Response;
      try {
        response = await this.fetchImpl(`${this.baseUrl}/v1/llm/chat?format=protocol`, {
          method: 'POST',
          signal: controller.signal,
          headers: this.headers({
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            'X-Message-Id': messageId,
          }),
          body: JSON.stringify({
            messages: request.messages,
            model: selection,
            ...(request.thinking ? { thinking: request.thinking } : {}),
            ...(request.effort ? { effort: request.effort } : {}),
            ...(request.conversation_id ? { conversation_id: request.conversation_id } : {}),
            format: 'protocol',
          }),
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        handlers.onError({
          message_id: messageId,
          code: 'INTERNAL',
          message: '无法连接 Worker（网络错误）',
          retryable: true,
        });
        return;
      }
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let body: ErrorEnvelope | null = null;
        try {
          body = text ? (JSON.parse(text) as ErrorEnvelope) : null;
        } catch {
          body = null;
        }
        const apiError = errorFromResponse(response.status, body);
        handlers.onError({
          message_id: messageId,
          code: apiError.code,
          message: apiError.message,
          retryable: apiError.retryable,
        });
        return;
      }
      const reader = response.body?.getReader();
      if (!reader) {
        handlers.onError({
          message_id: messageId,
          code: 'INTERNAL',
          message: 'Worker 返回了空响应',
          retryable: true,
        });
        return;
      }
      const decoder = new TextDecoder();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          dispatch(parser.push(decoder.decode(value, { stream: true })));
          if (parser.isDone) break;
        }
        dispatch(parser.finish());
      } catch (error) {
        if (controller.signal.aborted) return;
        handlers.onError({
          message_id: messageId,
          code: 'INTERNAL',
          message: '流式响应中断',
          retryable: true,
        });
      } finally {
        try {
          await reader.cancel();
        } catch {
          /* ignore */
        }
      }
    };

    void run();
    return { abort: () => controller.abort() };
  }
}

/** Convert a daemon/Worker `event` payload into a typed event (guarded). */
export function isEventPayload(value: unknown): value is EventPayload {
  return (
    typeof value === 'object' && value !== null && typeof (value as EventPayload).event === 'string'
  );
}
